import { DateTime } from 'luxon';

export const CALENDAR_DATE_FORMAT = 'yyyy-MM-dd';
export const TIME_VALUE_FORMAT = 'HH:mm';
const TIME_LABEL_FORMAT = 'h:mm a';
const DAY_LABEL_FORMAT = 'ccc, LLL d';

export const combineDateAndTime = (dateStr: string, timeStr: string): string =>
    DateTime.fromFormat(`${dateStr} ${timeStr}`, `${CALENDAR_DATE_FORMAT} ${TIME_VALUE_FORMAT}`).toISO() as string;

export const splitISO = (iso: string): { date: string; time: string } => {
    const dateTime = DateTime.fromISO(iso);
    return {
        date: dateTime.toFormat(CALENDAR_DATE_FORMAT),
        time: dateTime.toFormat(TIME_VALUE_FORMAT),
    };
};

export const addMinutesToISO = (iso: string, minutes: number): string =>
    DateTime.fromISO(iso).plus({ minutes }).toISO() as string;

export const isEndAfterStart = (startISO: string, endISO: string): boolean =>
    DateTime.fromISO(endISO) > DateTime.fromISO(startISO);

/**
 * Adds minutes to an HH:mm time, clamping to the last 15-min slot of the same day
 * so a late start (e.g. 11:45 PM) can't produce a next-day default end.
 */
export const addMinutesToTimeSameDay = (time: string, minutes: number): string => {
    const base = DateTime.fromFormat(time, TIME_VALUE_FORMAT);
    const added = base.plus({ minutes });
    return added.hasSame(base, 'day') ? added.toFormat(TIME_VALUE_FORMAT) : '23:45';
};

export const formatWhenLabel = (
    startISO?: string | null,
    endISO?: string | null,
    durationMinutes?: number | null,
): string | null => {
    if (!startISO) return null;

    const start = DateTime.fromISO(startISO);
    if (!start.isValid) return null;

    const end = durationMinutes ? start.plus({ minutes: durationMinutes }) : endISO ? DateTime.fromISO(endISO) : null;

    const startDay = start.toFormat(DAY_LABEL_FORMAT);

    if (!end || !end.isValid) {
        return start.hour === 0 && start.minute === 0 ? startDay : `${startDay} ${start.toFormat(TIME_LABEL_FORMAT)}`;
    }

    if (start.hasSame(end, 'day')) {
        return `${startDay} from ${start.toFormat(TIME_LABEL_FORMAT)} — ${end.toFormat(TIME_LABEL_FORMAT)}`;
    }

    return `${startDay} ${start.toFormat(TIME_LABEL_FORMAT)} — ${end.toFormat(DAY_LABEL_FORMAT)} ${end.toFormat(
        TIME_LABEL_FORMAT,
    )}`;
};
